import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import {
  Box,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Controller, useFormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'

import useFetch from 'hooks/useFetch'

import * as service from 'service'
import constants from 'constants/index'
import { ALL_DEPARTMENTS_OPTION, OTHER_TOOL_OPTION } from '../../formMappers'
import useStyles from './styles'

const { CATEGORY_CHIP_CLASS } = constants.aiInventory

const GeneralInfo = ({
  isShow = false,
  hideDepartment = false,
  currentDepartmentId = null,
  guestMode = false,
  toolsService = null,
  departmentsService = null,
  stepLabel = null,
  onNoEligibleDepartmentsChange = null,
}) => {
  const classes = useStyles()
  const { control, errors, watch, setValue } = useFormContext()

  const resolvedToolsService =
    toolsService || service.dponet.aiInventoryTools.get
  const resolvedDepartmentsService =
    departmentsService || service.dponet.aiInventory.getDepartments

  const [departmentInputValue, setDepartmentInputValue] = useState('')
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('')

  const formDepartment = guestMode ? watch('department') : null

  useEffect(() => {
    if (!guestMode) return
    setDepartmentInputValue(formDepartment?.name || '')
  }, [formDepartment?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(
      () =>
        setDepartmentSearchTerm(
          departmentInputValue.length >= 2 ? departmentInputValue : '',
        ),
      400,
    )
    return () => clearTimeout(timer)
  }, [departmentInputValue])

  const departmentFetchParams = {
    order: 'name ASC',
    ...(departmentSearchTerm ? { name: departmentSearchTerm } : {}),
  }

  const { response: responseDepartments, isLoading: isLoadingDepartments } =
    useFetch(
      resolvedDepartmentsService,
      departmentFetchParams,
      [departmentSearchTerm],
      hideDepartment ? !!departmentsService : true,
    )
  const { response: responseTools } = useFetch(resolvedToolsService)

  const departments = responseDepartments?.data?.departments || []
  // "Todos os departamentos" só faz sentido quando há mais de um departamento.
  const departmentOptions =
    departments.length > 1
      ? [ALL_DEPARTMENTS_OPTION, ...departments]
      : departments

  // "Todos os departamentos" é exclusivo: se escolhido, descarta os demais;
  // ao escolher um departamento específico, remove a opção "Todos".
  const resolveDepartmentSelection = (options) => {
    if (isEmpty(options)) return []
    const lastSelected = options[options.length - 1]
    if (lastSelected?.isAllDepartments) return [ALL_DEPARTMENTS_OPTION]
    return options.filter((option) => !option?.isAllDepartments)
  }

  useEffect(() => {
    if (!guestMode || !currentDepartmentId || formDepartment) return
    const matched = departments.find(
      (department) => department.id === currentDepartmentId,
    )
    if (matched) setValue('department', matched)
  }, [departments]) // eslint-disable-line react-hooks/exhaustive-deps
  const tools = responseTools?.data?.aiInventoryTools || []
  const toolOptions = [...tools, OTHER_TOOL_OPTION]

  const formSelectedTool = watch('selectedTool')
  const isOther = !!formSelectedTool?.isOther && !formSelectedTool?.id
  const selectedTool =
    !isOther && formSelectedTool?.id
      ? tools.find((tool) => tool.id === formSelectedTool.id) ||
        formSelectedTool
      : formSelectedTool
  const showToolDetails = selectedTool && !isOther

  const isDepartmentInvalid =
    hideDepartment &&
    currentDepartmentId &&
    !isEmpty(departments) &&
    !departments.find((department) => department.id === currentDepartmentId)

  // Lista vazia SEM busca ativa significa que o usuário não tem vínculo com
  // nenhum departamento — logo não pode cadastrar. Diferente de uma busca que
  // simplesmente não encontrou resultados (aí o noOptionsText do select basta).
  const hasNoEligibleDepartments =
    !hideDepartment &&
    !isLoadingDepartments &&
    !departmentSearchTerm &&
    !!responseDepartments &&
    isEmpty(departments)

  // O alerta de "sem departamento" é renderizado pelo formulário (acima do
  // card do step, mesmo local do banner de cookies), então só sinalizamos aqui.
  useEffect(() => {
    if (onNoEligibleDepartmentsChange) {
      onNoEligibleDepartmentsChange(hasNoEligibleDepartments)
    }
  }, [hasNoEligibleDepartments]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="overline">
            <strong>INFORMAÇÕES GERAIS</strong>
          </Typography>
          {stepLabel && (
            <Typography variant="subtitle1">{stepLabel}</Typography>
          )}
        </Box>
        <Typography variant="body2" color="textSecondary">
          Preencha as informações necessárias para adicionar uma nova IA ao
          inventário
        </Typography>
      </Box>
      <Divider />
      <Box p={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Inteligência Artificial
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Selecione a Inteligência Artificial utilizada pela empresa. Caso
              não encontre na lista, escolha a opção &quot;Outro&quot; para
              cadastrar manualmente.
            </Typography>
            <Controller
              name="selectedTool"
              control={control}
              onChange={([, option]) => option || null}
              as={
                <Autocomplete
                  options={toolOptions}
                  getOptionLabel={(option) => option.name || ''}
                  getOptionSelected={(option, selected) =>
                    option.id === selected.id &&
                    !!option.isOther === !!selected.isOther
                  }
                  disabled={isShow}
                  renderOption={(option) => (
                    <Box className={classes.menuItemContent}>
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          alt={option.name}
                          className={classes.toolIcon}
                        />
                      )}
                      <Typography>{option.name}</Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecione ou busque uma Inteligência Artificial"
                      variant="outlined"
                      error={!!errors.selectedTool}
                      helperText={errors.selectedTool?.message}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selectedTool?.iconUrl ? (
                          <InputAdornment position="start">
                            <img
                              src={selectedTool.iconUrl}
                              alt={selectedTool.name}
                              className={classes.toolIcon}
                            />
                          </InputAdornment>
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              }
            />
          </Grid>

          {isOther && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Nome da Inteligência Artificial
                </Typography>
                <Controller
                  name="toolName"
                  control={control}
                  as={
                    <TextField
                      placeholder="Insira o nome da Inteligência Artificial"
                      fullWidth
                      variant="outlined"
                      error={!!errors.toolName}
                      helperText={errors.toolName?.message}
                      disabled={isShow}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Nome do provedor
                </Typography>
                <Controller
                  name="providerName"
                  control={control}
                  as={
                    <TextField
                      placeholder="Insira o nome do provedor"
                      fullWidth
                      variant="outlined"
                      error={!!errors.providerName}
                      helperText={errors.providerName?.message}
                      disabled={isShow}
                    />
                  }
                />
              </Grid>
            </>
          )}

          {showToolDetails && (
            <Grid item xs={12}>
              <Box className={classes.toolMetaStrip}>
                {selectedTool.aiInventoryProvider && (
                  <Box className={classes.metaProviderColumn}>
                    <Typography variant="overline">Provedor</Typography>
                    <Box className={classes.providerBadge}>
                      {selectedTool.aiInventoryProvider.iconUrl && (
                        <img
                          src={selectedTool.aiInventoryProvider.iconUrl}
                          alt={selectedTool.aiInventoryProvider.name}
                          className={classes.providerBadgeIcon}
                        />
                      )}
                      <Typography className={classes.providerBadgeText}>
                        {selectedTool.aiInventoryProvider.name}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!isEmpty(selectedTool.aiInventoryCategories) && (
                  <Box className={classes.metaCategoriesColumn}>
                    <Typography variant="overline">Categorias</Typography>
                    <Box className={classes.categoriesRow}>
                      {selectedTool.aiInventoryCategories.map((category) => (
                        <Chip
                          key={category.id}
                          label={category.name}
                          size="small"
                          className={
                            classes[CATEGORY_CHIP_CLASS[category.slug]] ||
                            classes.chipDefault
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Descrição de funcionalidades
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Descreva como a IA é utilizada no dia a dia da organização
            </Typography>
            <Controller
              name="description"
              control={control}
              as={
                <TextField
                  placeholder="Ex.: utilizada para geração de textos, revisão de conteúdo e sugestões automáticas"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  disabled={isShow}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              }
            />
          </Grid>

          {!hideDepartment && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Local de Ocorrência
              </Typography>
              {!isDepartmentInvalid &&
                !currentDepartmentId &&
                !hasNoEligibleDepartments && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Aponte quais departamentos utilizam IA dentro da sua
                    organização
                  </Typography>
                )}
              {isDepartmentInvalid && (
                <Box p={2} mb={1} className={classes.invalidDepartmentWarning}>
                  <Typography
                    variant="body2"
                    className={classes.invalidDepartmentText}
                  >
                    O departamento associado ao seu perfil foi removido ou
                    inativado. Selecione um novo departamento para continuar.
                  </Typography>
                </Box>
              )}
              {guestMode ? (
                <Controller
                  name="department"
                  control={control}
                  onChange={([, option]) => option || null}
                  as={
                    <Autocomplete
                      options={departments}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, selected) =>
                        option.id === selected.id
                      }
                      filterOptions={(options) => options}
                      loading={isLoadingDepartments}
                      loadingText="Carregando..."
                      inputValue={departmentInputValue}
                      onInputChange={(_, value, reason) => {
                        if (reason !== 'reset') setDepartmentInputValue(value)
                      }}
                      disabled={
                        isShow ||
                        (!!currentDepartmentId && !isDepartmentInvalid)
                      }
                      noOptionsText="Nenhum departamento encontrado"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Selecione ou busque um departamento"
                          variant="outlined"
                          error={!!errors.department}
                          helperText={errors.department?.message}
                        />
                      )}
                    />
                  }
                />
              ) : (
                <Tooltip
                  title={
                    hasNoEligibleDepartments
                      ? 'Seu usuário não está vinculado a nenhum departamento. Solicite a um responsável da empresa para liberar o cadastro de IA.'
                      : ''
                  }
                >
                  <span style={{ display: 'block' }}>
                    <Controller
                      name="department"
                      control={control}
                      onChange={([, options]) =>
                        resolveDepartmentSelection(options || [])
                      }
                      as={
                        <Autocomplete
                          multiple
                          options={departmentOptions}
                          getOptionLabel={(option) => option.name || ''}
                          getOptionSelected={(option, selected) =>
                            option.id === selected.id
                          }
                          filterOptions={(options) => options}
                          loading={isLoadingDepartments}
                          loadingText="Carregando..."
                          inputValue={departmentInputValue}
                          onInputChange={(_, value, reason) => {
                            if (reason !== 'reset')
                              setDepartmentInputValue(value)
                          }}
                          disabled={isShow || hasNoEligibleDepartments}
                          noOptionsText="Nenhum departamento encontrado"
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={option.id}
                                label={option.name}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Selecione ou busque departamentos"
                              variant="outlined"
                              error={!!errors.department}
                              helperText={errors.department?.message}
                            />
                          )}
                        />
                      }
                    />
                  </span>
                </Tooltip>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    </Paper>
  )
}

GeneralInfo.propTypes = {
  isShow: PropTypes.bool,
  hideDepartment: PropTypes.bool,
  currentDepartmentId: PropTypes.number,
  guestMode: PropTypes.bool,
  toolsService: PropTypes.func,
  departmentsService: PropTypes.func,
  stepLabel: PropTypes.string,
  onNoEligibleDepartmentsChange: PropTypes.func,
}

export default GeneralInfo
